import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { resolveImage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Image as ImageIcon, Upload, Loader2, X } from "lucide-react";

export default function MediaPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState("");

  const load = async () => {
    setLoading(true);
    try { setAssets((await api.get("/admin/media")).data || []); }
    catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      await load();
      pick(data.url);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const pick = (u) => { onChange(u); setOpen(false); };

  return (
    <div>
      <div className="flex items-center gap-2">
        {value ? (
          <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200 shrink-0">
            <img src={resolveImage(value)} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange("")} className="absolute top-0 right-0 bg-black/60 text-white rounded-bl p-0.5" data-testid="media-clear">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="w-16 h-16 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-slate-300 shrink-0">
            <ImageIcon className="w-5 h-5" />
          </div>
        )}
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="media-pick-btn">Select media</Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Media Library</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2">
            <Input placeholder="Paste image URL…" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="media-url-input" />
            <Button type="button" size="sm" onClick={() => url && pick(url)} disabled={!url}>Use URL</Button>
            <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-teal text-white text-sm cursor-pointer whitespace-nowrap">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
              <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="media-upload-input" />
            </label>
          </div>
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-teal mx-auto" /></div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-80 overflow-y-auto">
              {assets.map((a) => (
                <button key={a.id} type="button" onClick={() => pick(a.url)} className="aspect-square rounded-lg overflow-hidden border border-slate-200 hover:border-teal hover:ring-2 hover:ring-teal/40 transition" data-testid={`media-asset-${a.id}`}>
                  <img src={resolveImage(a.url)} alt={a.filename} className="w-full h-full object-cover" />
                </button>
              ))}
              {!assets.length && <p className="col-span-full text-center text-sm text-slate-400 py-8">No media yet. Upload or paste a URL above.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
